import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useDebounce } from 'react-use';

import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import type { AdvancedFilterDefinition } from '@/shared/components/filters';
import {
    workspaceService,
    type AuditLogQueryFacets,
    type AuditLogQueryInput,
    type AuditLogQueryMode,
} from '@/services/database';
import type { AuditLog, Member } from '@/types';

import { toAuditLogRequestAdvancedFilter, toAuditLogRequestFilters } from '../auditLogFilters';

interface UsePaginatedAuditLogsInput {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    enabled: boolean;
    filters: ListFilterRule[];
    mode: AuditLogQueryMode;
    search: string;
    workspaceId?: string;
}

const PAGE_SIZE = 40;

const mergeAuditLogs = (current: AuditLog[], incoming: AuditLog[]) => {
    const logsById = new Map(current.map((log) => [log.id, log]));

    incoming.forEach((log) => logsById.set(log.id, log));

    return Array.from(logsById.values());
};

export const usePaginatedAuditLogs = ({
    advancedFilter,
    enabled,
    filters,
    mode,
    search,
    workspaceId,
}: UsePaginatedAuditLogsInput) => {
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [filterOptionLogs, setFilterOptionLogs] = useState<AuditLog[]>([]);
    const [facets, setFacets] = useState<AuditLogQueryFacets>();
    const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string>();
    const [total, setTotal] = useState(0);
    const [actionTakenTotal, setActionTakenTotal] = useState(0);
    const generationRef = useRef(0);
    const workspaceRef = useRef(workspaceId);

    useDebounce(() => setDebouncedSearch(search.trim()), 300, [search]);

    const requestBase = useMemo<AuditLogQueryInput>(
        () => ({
            advancedFilter: toAuditLogRequestAdvancedFilter(advancedFilter),
            filters: toAuditLogRequestFilters(filters),
            limit: PAGE_SIZE,
            mode,
            search: debouncedSearch,
        }),
        [advancedFilter, debouncedSearch, filters, mode],
    );

    useEffect(() => {
        const generation = ++generationRef.current;

        if (workspaceRef.current !== workspaceId) {
            workspaceRef.current = workspaceId;
            setFilterOptionLogs([]);
            setFacets(undefined);
        }

        setAuditLogs([]);
        setLoadingMore(false);
        setHasMore(false);
        setNextCursor(undefined);
        setTotal(0);
        setActionTakenTotal(0);

        if (!enabled || !workspaceId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        void workspaceService
            .queryAuditLogs(workspaceId, requestBase)
            .then((page) => {
                if (generationRef.current !== generation) {return;}

                setAuditLogs(page.items);
                setFilterOptionLogs((current) => mergeAuditLogs(current, page.items));
                setFacets(page.facets);
                setHasMore(page.hasMore);
                setNextCursor(page.nextCursor);
                setTotal(page.total ?? 0);
                setActionTakenTotal(page.actionTakenTotal ?? 0);
            })
            .catch((error) => {
                if (generationRef.current === generation) {captureException(error);}
            })
            .finally(() => {
                if (generationRef.current === generation) {setLoading(false);}
            });

        return () => {
            if (generationRef.current === generation) {generationRef.current += 1;}
        };
    }, [enabled, requestBase, workspaceId]);

    const loadMore = useCallback(async () => {
        if (!enabled || !workspaceId || !hasMore || !nextCursor || loading || loadingMore) {return;}

        const generation = generationRef.current;

        try {
            setLoadingMore(true);

            const page = await workspaceService.queryAuditLogs(workspaceId, {
                ...requestBase,
                cursor: nextCursor,
            });

            if (generationRef.current !== generation) {return;}

            setAuditLogs((current) => mergeAuditLogs(current, page.items));
            setFilterOptionLogs((current) => mergeAuditLogs(current, page.items));
            setHasMore(page.hasMore);
            setNextCursor(page.nextCursor);
            if (page.total !== undefined) {setTotal(page.total);}
            if (page.actionTakenTotal !== undefined) {setActionTakenTotal(page.actionTakenTotal);}
        } catch (error) {
            if (generationRef.current === generation) {captureException(error);}
        } finally {
            if (generationRef.current === generation) {setLoadingMore(false);}
        }
    }, [enabled, hasMore, loading, loadingMore, nextCursor, requestBase, workspaceId]);

    const updateMember = useCallback((updatedMember: Member) => {
        const updateLogs = (logs: AuditLog[]) =>
            logs.map((log) => {
                if (log.member?.id !== updatedMember.id) {return log;}

                return {
                    ...log,
                    member: updatedMember,
                    user:
                        log.user?.id === updatedMember.user?.id
                            ? { ...log.user, activeMember: updatedMember }
                            : log.user,
                };
            });

        setAuditLogs(updateLogs);
        setFilterOptionLogs(updateLogs);
    }, []);

    return {
        actionTakenTotal,
        auditLogs,
        facets,
        filterOptionLogs,
        hasMore,
        loadMore,
        loading,
        loadingMore,
        total,
        updateMember,
    };
};
